import { useState, type DragEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { Board, Priority } from '@cortex/domain-org-issues';
import { Badge } from '@cortex/web-design-system/components/ui/Badge';
import { useMoveOrgIssue } from '../../../hooks/useOrgIssues.js';
import type { OrgIssuePeople, OrgIssueView } from '../../../lib/api/org-issues.js';
import {
  isOverdue,
  localToday,
  personLabel,
  summarizeActions,
  toKanbanLanes,
} from '../../../lib/org-issues.js';

/** 優先度のバッジの色。 @graph-connects none */
const PRIORITY_VARIANTS: Record<Priority, 'error' | 'warning' | 'default'> = {
  high: 'error',
  medium: 'warning',
  low: 'default',
};

/** ドラッグで運ぶ課題 ID の型。 @graph-connects none */
const DRAG_TYPE = 'application/x-org-issue';

/** @graph-connects none */
interface KanbanBoardProps {
  board: Board;
  issues: readonly OrgIssueView[];
  people: OrgIssuePeople;
  onOpen: (issueId: string) => void;
}

/**
 * 看板。列ごとにカードを並べ、ドラッグで列を移す（状態変更）。
 *
 * **動かせるかは API が課題ごとに返す `permissions.canMove` だけで決める。** 動かせない
 * カードはドラッグできないようにするが、それは見え方の制御で、通ってしまっても API が
 * 403 で止め、その文言をそのまま出す。
 *
 * @graph-stack product-innovation-lab
 * @graph-domain Engineering
 * @graph-business 組織課題を列（状態）ごとのカードで並べ、ピン留め・優先度・担当・Next の進みを見せ、動かせる課題はドラッグで列を移す
 * @graph-connects useMoveOrgIssue [calls] 列の移動
 * @graph-connects toKanbanLanes [calls] 列への振り分け
 */
export function KanbanBoard({ board, issues, people, onOpen }: KanbanBoardProps): ReactNode {
  const { t } = useTranslation();
  const move = useMoveOrgIssue(board.id);
  const [overColumn, setOverColumn] = useState<string | null>(null);
  const lanes = toKanbanLanes(board, issues);
  const movable = new Map(issues.map((v) => [v.issue.id, v]));

  const drop = (e: DragEvent<HTMLElement>, columnId: string) => {
    e.preventDefault();
    setOverColumn(null);
    const issueId = e.dataTransfer.getData(DRAG_TYPE);
    const view = movable.get(issueId);
    if (view === undefined || view.issue.columnId === columnId) return;
    move.mutate({ issueId, columnId });
  };

  return (
    <div className="space-y-2">
      {move.isError && (
        <p className="whitespace-pre-wrap text-sm text-[var(--color-error)]" role="alert">
          {t('orgIssues.board.moveFailed', { message: move.error.message })}
        </p>
      )}
      <div className="flex gap-3 overflow-x-auto pb-2">
        {lanes.map(({ column, issues: laneIssues }) => (
          <section
            key={column.id}
            aria-label={column.label}
            className={`flex w-72 shrink-0 flex-col gap-2 rounded-lg border p-2 ${
              overColumn === column.id
                ? 'border-[var(--color-accent-primary)] bg-[var(--color-accent-primary)]/5'
                : 'border-[var(--color-border)] bg-[var(--color-bg-base)]'
            }`}
            onDragOver={(e) => {
              if (!e.dataTransfer.types.includes(DRAG_TYPE)) return;
              e.preventDefault();
              setOverColumn(column.id);
            }}
            onDragLeave={() => {
              setOverColumn((current) => (current === column.id ? null : current));
            }}
            onDrop={(e) => {
              drop(e, column.id);
            }}
          >
            <header className="flex items-center justify-between px-1">
              <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">
                {column.label}
              </h2>
              <span className="text-xs text-[var(--color-text-muted)]">
                {t('orgIssues.board.count', { count: laneIssues.length })}
              </span>
            </header>
            {laneIssues.length === 0 && (
              <p className="px-1 py-4 text-center text-xs text-[var(--color-text-muted)]">
                {t('orgIssues.board.emptyColumn')}
              </p>
            )}
            {laneIssues.map((view) => (
              <IssueCard
                key={view.issue.id}
                view={view}
                people={people}
                onOpen={() => {
                  onOpen(view.issue.id);
                }}
              />
            ))}
          </section>
        ))}
      </div>
    </div>
  );
}

/** @graph-connects none */
interface IssueCardProps {
  view: OrgIssueView;
  people: OrgIssuePeople;
  onOpen: () => void;
}

/**
 * 課題のカード。押すと詳細を開く。
 * @graph-connects summarizeActions [calls] Next の要約
 */
function IssueCard({ view, people, onOpen }: IssueCardProps): ReactNode {
  const { t } = useTranslation();
  const { issue, permissions } = view;
  const next = summarizeActions(issue.actions);
  const overdue = isOverdue(next.nextDueOn, localToday());

  return (
    <button
      type="button"
      draggable={permissions.canMove}
      onDragStart={(e) => {
        e.dataTransfer.setData(DRAG_TYPE, issue.id);
        e.dataTransfer.effectAllowed = 'move';
      }}
      onClick={onOpen}
      className={`w-full space-y-1.5 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-2 text-left shadow-sm hover:border-[var(--color-accent-primary)] ${
        permissions.canMove ? 'cursor-grab' : 'cursor-pointer'
      }`}
    >
      <div className="flex flex-wrap items-center gap-1">
        {issue.pinned && <span aria-label={t('orgIssues.card.pinned')}>📌</span>}
        {issue.priority !== null && (
          <Badge size="sm" variant={PRIORITY_VARIANTS[issue.priority]}>
            {t(`orgIssues.priority.${issue.priority}`)}
          </Badge>
        )}
        {issue.sharing !== 'shared' && (
          <Badge size="sm">{t(`orgIssues.sharing.${issue.sharing}`)}</Badge>
        )}
      </div>
      <p className="text-sm font-medium text-[var(--color-text-primary)]">{issue.title}</p>
      {issue.assignees.length > 0 && (
        <p className="text-xs text-[var(--color-text-muted)]">
          {issue.assignees.map((id) => personLabel(people, id)).join(', ')}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-1 text-xs text-[var(--color-text-muted)]">
        {next.total > 0 && (
          <span>{t('orgIssues.card.next', { done: next.done, total: next.total })}</span>
        )}
        {next.nextDueOn !== null && (
          <span className={overdue ? 'text-[var(--color-error)]' : ''}>{next.nextDueOn}</span>
        )}
        {issue.labels.map((label) => (
          <Badge key={label} size="sm" variant="info">
            {label}
          </Badge>
        ))}
      </div>
    </button>
  );
}
